import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { adminApi, type MountPurpose, type MountPurposeSortField } from '../api/admin';
import { DataListFrame, sortArrow, useListControls } from '../components/data-list';

// 用途マスタ: device-mount の「用途」の選択肢を管理する小マスター。
// フラット（区分のような従属はなし）。label / sort / enabled の単純 CRUD。
// 検索/並び替え/ページングは常にサーバ側（お手本: エージェント一覧）。
const PURPOSES_KEY = ['mount-purposes'];

// 並び替え可能列（backend MOUNT_PURPOSE_SORT_FIELDS と一致）。
const SORT_OPTIONS: { value: MountPurposeSortField; label: string }[] = [
  { value: 'sort', label: '順' },
  { value: 'label', label: '用途メモ（表示名）' },
  { value: 'id', label: 'ID' },
];

export function MountPurposesPage() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: PURPOSES_KEY });

  const controls = useListControls<MountPurposeSortField>('konecco.list.mount-purposes', {
    q: '',
    sortBy: 'sort',
    sortDir: 'asc',
    page: 1,
    pageSize: 20,
  });
  // 有効/無効はマスタ的（選択肢固定）= ドロップダウン絞り込み。'' は指定なし。
  const [enabledFilter, setEnabledFilter] = useState<'' | 'true' | 'false'>('');

  const { data, isLoading } = useQuery({
    queryKey: [
      ...PURPOSES_KEY,
      'search',
      controls.q,
      enabledFilter,
      controls.sortBy,
      controls.sortDir,
      controls.page,
      controls.pageSize,
    ],
    queryFn: () =>
      adminApi.searchMountPurposes({
        q: controls.q || undefined,
        enabled: enabledFilter === '' ? undefined : enabledFilter === 'true',
        sortBy: controls.sortBy,
        sortDir: controls.sortDir,
        page: controls.page,
        pageSize: controls.pageSize,
      }),
    placeholderData: keepPreviousData,
  });

  const [newOpen, setNewOpen] = useState(false);

  const purposes = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p className="k-muted" style={{ margin: 0, fontSize: 13.5 }}>
        デバイスマウントの「用途メモ」の選択肢を管理します。
      </p>

      {newOpen && (
        <PurposeForm
          onClose={() => setNewOpen(false)}
          onSubmit={async (v) => {
            await adminApi.createMountPurpose(v);
            await invalidate();
            setNewOpen(false);
          }}
        />
      )}

      <DataListFrame
        title="用途メモマスタ"
        total={total}
        loading={isLoading}
        controls={controls}
        sortOptions={SORT_OPTIONS}
        searchPlaceholder="あいまい検索…（用途メモ名）"
        filters={
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="k-muted" style={{ fontSize: 12 }}>
              状態
            </span>
            <select
              value={enabledFilter}
              onChange={(e) => {
                setEnabledFilter(e.target.value as '' | 'true' | 'false');
                controls.setPage(1);
              }}
              style={{ padding: '7px 10px', fontSize: 13 }}
            >
              <option value="">すべて</option>
              <option value="true">有効</option>
              <option value="false">無効</option>
            </select>
          </label>
        }
        rightActions={
          <button type="button" onClick={() => setNewOpen(true)}>
            ＋ 用途メモを追加
          </button>
        }
      >
        <table className="k-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th
                style={{ textAlign: 'left', width: 60, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => controls.toggleSort('sort')}
                title="クリックで並び替え"
              >
                順{sortArrow(controls, 'sort')}
              </th>
              <th
                style={{ textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => controls.toggleSort('label')}
                title="クリックで並び替え"
              >
                用途メモ（表示名）{sortArrow(controls, 'label')}
              </th>
              <th style={{ textAlign: 'left', width: 80 }}>状態</th>
              <th style={{ width: 140 }}></th>
            </tr>
          </thead>
          <tbody>
            {purposes.map((p) => (
              <PurposeRow key={p.id} purpose={p} onChanged={invalidate} />
            ))}
          </tbody>
        </table>
      </DataListFrame>
    </div>
  );
}

function PurposeRow({ purpose, onChanged }: { purpose: MountPurpose; onChanged: () => void }) {
  const [editOpen, setEditOpen] = useState(false);
  const del = useMutation({
    mutationFn: () => adminApi.deleteMountPurpose(purpose.id),
    onSuccess: onChanged,
  });
  return (
    <>
      <tr>
        <td>{purpose.sort}</td>
        <td>{purpose.label}</td>
        <td>{purpose.enabled ? '有効' : <span style={{ color: '#b45309' }}>無効</span>}</td>
        <td style={{ textAlign: 'right' }}>
          <button type="button" className="btn-ghost" onClick={() => setEditOpen(true)}>
            編集
          </button>{' '}
          <button
            type="button"
            className="btn-danger"
            onClick={() => {
              if (window.confirm(`用途メモ「${purpose.label}」を削除しますか？`)) del.mutate();
            }}
          >
            削除
          </button>
        </td>
      </tr>
      {editOpen && (
        <tr>
          <td colSpan={4}>
            <PurposeForm
              initial={purpose}
              onClose={() => setEditOpen(false)}
              onSubmit={async (v) => {
                await adminApi.updateMountPurpose(purpose.id, v);
                onChanged();
                setEditOpen(false);
              }}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ── フォーム（用途）。label + sort + enabled ──
function PurposeForm({
  initial,
  onClose,
  onSubmit,
}: {
  initial?: { label: string; sort: number; enabled: boolean };
  onClose: () => void;
  onSubmit: (v: { label: string; sort: number; enabled: boolean }) => Promise<void>;
}) {
  const [label, setLabel] = useState(initial?.label ?? '');
  const [sort, setSort] = useState(initial?.sort ?? 0);
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const editing = !!initial;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await onSubmit({ label: label.trim(), sort: Number(sort) || 0, enabled });
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={formBox}>
      <strong>{editing ? '用途メモを編集' : '用途メモを追加'}</strong>
      <div style={fieldRow}>
        <input placeholder="表示名（例: 開発）" value={label} onChange={(e) => setLabel(e.target.value)} />
        <input
          type="number"
          placeholder="順"
          value={sort}
          onChange={(e) => setSort(Number(e.target.value))}
          style={{ width: 80 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> 有効
        </label>
      </div>
      {err && <p style={{ color: '#dc2626', margin: 0, fontSize: 13 }}>{err}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" disabled={busy || !label.trim()}>
          保存
        </button>
        <button type="button" className="btn-ghost" onClick={onClose}>
          キャンセル
        </button>
      </div>
    </form>
  );
}

const formBox: React.CSSProperties = {
  marginTop: 12,
  padding: 14,
  border: '1px solid #dbe3ee',
  borderRadius: 10,
  background: '#f8fafc',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};
const fieldRow: React.CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' };
